import * as THREE from 'three';
import { RevolveAngleGizmoPartKind } from './RevolveAngleGizmoPartKind';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/mesh/feature/revolve 层；
 * 2. 负责封装旋转角度手柄中的单个可拾取部件；
 * 3. 该对象用 class 字段表达部件语义，禁止通过散落 userData 传递业务含义。
 *
 * 文件职责：
 * 1. 创建方向 guide line 与 cone 拖拽头；
 * 2. 根据半径、角度与尺寸刷新显示；
 * 3. 提供 dispose 释放 geometry/material。
 */

/**
 * 旋转角度手柄部件。
 */
export class RevolveAnglePartGroup extends THREE.Group {
    /** 部件类型。 */
    public readonly partKind: RevolveAngleGizmoPartKind;
    /** 引导线。 */
    private readonly guideLine: THREE.Line;
    /** 拖拽头。 */
    private readonly headMesh: THREE.Mesh;
    /** 引导线点坐标缓冲。 */
    private readonly guideLinePositions = new Float32Array(6);
    /** 引导线包围球，避免热路径重复 new。 */
    private readonly guideLineBoundingSphere = new THREE.Sphere();
    /** 拖拽头终点复用对象。 */
    private readonly endPoint = new THREE.Vector3();
    /** 拖拽头切向复用对象。 */
    private readonly tangent = new THREE.Vector3();
    /** 箭头默认朝向复用对象。 */
    private readonly arrowDefaultDirection = new THREE.Vector3(0, 1, 0);

    /**
     * @param input 构造输入。
     */
    public constructor(input: {
        partKind: RevolveAngleGizmoPartKind;
        color: number;
    }) {
        super();
        this.partKind = input.partKind;
        this.guideLine = this.createGuideLine(input.color);
        this.headMesh = this.createArrowHead(input.color);
        this.add(this.guideLine);
        this.add(this.headMesh);
    }

    /**
     * 读取当前命中的手柄部件。
     *
     * @param object 命中对象。
     * @returns 手柄部件；未命中时返回 null。
     */
    public static resolveFromObject(object: THREE.Object3D | null): RevolveAnglePartGroup | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (current instanceof RevolveAnglePartGroup) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 读取当前命中的可拖拽手柄部件。
     *
     * 设计说明：
     * 1. SolidWorks 旧实现只允许拖拽手柄头，guide line 只做视觉指引；
     * 2. 这里通过 class 私有字段判断命中对象，避免回退到散落 userData；
     * 3. 命中 guide line 时返回 null，防止拖拽手感和旧分支不一致。
     *
     * @param object 命中对象。
     * @returns 可拖拽手柄部件；未命中手柄头时返回 null。
     */
    public static resolveDraggableFromObject(object: THREE.Object3D | null): RevolveAnglePartGroup | null {
        let current: THREE.Object3D | null = object;
        while (current?.parent) {
            if (current.parent instanceof RevolveAnglePartGroup) {
                return current === current.parent.headMesh ? current.parent : null;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 更新部件显示。
     *
     * @param input 显示输入。
     */
    public updateVisual(input: {
        radius: number;
        headSize: number;
        angle: number;
        visible: boolean;
        directionSign: 1 | -1;
    }): void {
        const signedAngle = input.angle * input.directionSign;
        const radius = Math.max(1e-4, input.radius);
        const headSize = Math.max(1e-4, input.headSize);
        this.endPoint.set(
            Math.cos(signedAngle) * radius,
            Math.sin(signedAngle) * radius,
            0,
        );
        this.tangent.set(
            -Math.sin(signedAngle),
            Math.cos(signedAngle),
            0,
        ).multiplyScalar(input.directionSign).normalize();

        this.setLineEndPoint(this.endPoint);
        this.headMesh.position.copy(this.endPoint);
        this.headMesh.scale.set(headSize * 0.6, headSize, headSize * 0.6);
        this.orientArrow(this.tangent);
        this.visible = input.visible;
    }

    /**
     * 释放部件资源。
     */
    public dispose(): void {
        this.disposeLine(this.guideLine);
        this.disposeMesh(this.headMesh);
    }

    /**
     * 创建引导线。
     *
     * @param color 颜色。
     * @returns 引导线。
     */
    private createGuideLine(color: number): THREE.Line {
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(this.guideLinePositions, 3));
        geometry.boundingSphere = this.guideLineBoundingSphere;
        const line = new THREE.Line(
            geometry,
            new THREE.LineBasicMaterial({
                color,
                transparent: true,
                opacity: 0.9,
                depthTest: false,
                depthWrite: false,
            }),
        );
        line.renderOrder = 81;
        return line;
    }

    /**
     * 创建拖拽头。
     *
     * @param color 颜色。
     * @returns 拖拽头。
     */
    private createArrowHead(color: number): THREE.Mesh {
        const head = new THREE.Mesh(
            new THREE.ConeGeometry(1, 1, 16),
            new THREE.MeshStandardMaterial({
                color,
                metalness: 0.42,
                roughness: 0.58,
                emissive: 0x20242a,
                emissiveIntensity: 0.02,
                transparent: true,
                opacity: 0.92,
                depthTest: false,
                depthWrite: false,
            }),
        );
        head.renderOrder = 82;
        return head;
    }

    /**
     * 更新引导线终点。
     *
     * 设计说明：
     * 1. 手柄会在鼠标移动时高频刷新；
     * 2. 因此只更新已有的 BufferAttribute，避免热路径中重建几何体。
     *
     * @param endPoint 终点。
     */
    private setLineEndPoint(endPoint: THREE.Vector3): void {
        const position = this.guideLine.geometry.getAttribute('position') as THREE.BufferAttribute;
        position.setXYZ(0, 0, 0, 0);
        position.setXYZ(1, endPoint.x, endPoint.y, endPoint.z);
        position.needsUpdate = true;
        this.guideLineBoundingSphere.center.set(endPoint.x * 0.5, endPoint.y * 0.5, endPoint.z * 0.5);
        this.guideLineBoundingSphere.radius = Math.max(1e-4, endPoint.length() * 0.5);
    }

    /**
     * 调整箭头朝向。
     *
     * @param directionLocal 局部方向。
     */
    private orientArrow(directionLocal: THREE.Vector3): void {
        const forward = directionLocal.lengthSq() <= 1e-12
            ? this.arrowDefaultDirection
            : directionLocal;
        this.headMesh.quaternion.setFromUnitVectors(this.arrowDefaultDirection, forward);
    }

    /**
     * 释放线资源。
     *
     * @param line 线对象。
     */
    private disposeLine(line: THREE.Line): void {
        line.geometry.dispose();
        const material = line.material;
        if (Array.isArray(material)) {
            material.forEach((item) => item.dispose());
            return;
        }
        material.dispose();
    }

    /**
     * 释放网格资源。
     *
     * @param mesh 网格对象。
     */
    private disposeMesh(mesh: THREE.Mesh): void {
        mesh.geometry.dispose();
        const material = mesh.material;
        if (Array.isArray(material)) {
            material.forEach((item) => item.dispose());
            return;
        }
        material.dispose();
    }
}
